import { Alert, Box, Paper, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { earliestDateInBlob, SHIFTS_DATE_COLUMN } from "../../loadOrder";
import { type LoadRecurringResult, LoadResultDisplay, type LoadShiftsResult } from "../LoadResultDisplay";
import { NurseLoadSideEffects } from "../NurseLoadSideEffects";
import type { LoadNursesResults } from "../NursesSummary";
import { PreviewCard } from "../PreviewCard";
import { RosterLoadResults } from "../RosterLoadResults";
import { type DataLoadFile, fileTypeOf, KIND_LABEL, MODE_LABEL, ROSTER_ACTION_LABEL } from "./consts";

type Props = {
	file: DataLoadFile;
	index: number;
	departmentId: string | undefined;
	timezone: string | undefined;
	isNextInOrder: boolean;
	isLoading: boolean;
	/**
	 * True while the sequential load chain is running. It iterates a snapshot taken before the run, so
	 * removing a file mid-chain would still load it while its result and any error were discarded
	 * against an id no longer in state.
	 */
	locked: boolean;
	onSkipChange: (skip: boolean) => void;
	onFilteredBlobChange: (blob: Blob) => void;
	onEarliestDateChange: (date: DateTime | null) => void;
	onUpload: () => void;
	onRemove: () => void;
};

const statusSuffix = (file: DataLoadFile): string => {
	if (file.loadResult) return " — uploaded";
	if (file.skip) return " — skipped";
	if (file.loadError) return " — load failed";
	return "";
};

export function DataLoadResultCard({
	file,
	index,
	departmentId,
	timezone,
	isNextInOrder,
	isLoading,
	locked,
	onSkipChange,
	onFilteredBlobChange,
	onEarliestDateChange,
	onUpload,
	onRemove,
}: Props) {
	const testId = `data-load-result-${file.id}`;
	// `buildOrderedFiles` drops files with no kind and Run is gated on there being none, so this is
	// unreachable. It stays a hard exit rather than a default: guessing a kind here would render the
	// wrong preview and hand the file to the wrong loader.
	if (!file.kind) return null;

	const heading = `${index + 1}. ${KIND_LABEL[file.kind]}${
		file.kind === "roster" ? ` (${ROSTER_ACTION_LABEL[file.rosterAction].toLowerCase()})` : ""
	}: ${file.file.name}${statusSuffix(file)}`;

	if (file.transformError) {
		return (
			<Alert
				severity="error"
				data-testid={`${testId}-transform-error`}
				action={
					<SystemButton buttonVariant="borderless" onClick={onRemove} disabled={locked} data-testid={`${testId}-remove`}>
						Remove
					</SystemButton>
				}
			>
				{`${file.file.name} failed to transform: ${file.transformError}`}
			</Alert>
		);
	}

	const uploadLabel = file.loadResult ? "Uploaded" : isLoading ? "Uploading..." : isNextInOrder ? "Upload" : "Upload (waiting its turn)";
	const uploadDisabled = isLoading || file.loadResult !== null || file.skip || !isNextInOrder;

	return (
		<Box data-testid={testId}>
			<Typography variant="subtitle2" sx={{ mb: 0.5, color: "text.secondary" }}>
				{heading}
			</Typography>

			{file.loadError && (
				<Alert severity="error" sx={{ mb: 1 }} data-testid={`${testId}-load-error`}>
					{`Load failed: ${file.loadError}. Fix the cause and load again — this file is still next in line.`}
				</Alert>
			)}

			{file.mode === "transform" && file.transformedData ? (
				<PreviewCard
					fileName={file.file.name}
					fileType={fileTypeOf(file.kind)}
					transformedData={file.transformedData}
					dataTestIdPrefix={`${testId}-preview`}
					failedTabs={file.failedTabs}
					skip={file.skip}
					onSkipChange={locked ? () => undefined : onSkipChange}
					onCancel={locked ? () => undefined : onRemove}
					onFilteredBlobChange={(blob) => {
						onFilteredBlobChange(blob);
						// Follows the filtered payload: dropping a file's earliest rows raises its earliest
						// date, which can move it past another file in the load order.
						if (file.kind === "shifts") void earliestDateInBlob(blob, SHIFTS_DATE_COLUMN).then(onEarliestDateChange);
					}}
					uploadButtons={[{ label: uploadLabel, onClick: onUpload, disabled: uploadDisabled, loading: isLoading }]}
				/>
			) : (
				/* No preview for an as-is upload: a PreviewCard implies the FILTERED blob is what gets
				   sent, which would quietly turn "as-is" into a transform. */
				<Paper variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
					<Box sx={{ flex: 1, minWidth: 200 }}>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{file.file.name}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{`${MODE_LABEL.direct} — uploaded exactly as provided, with no transform.`}
						</Typography>
					</Box>
					<SystemSwitch
						checked={file.skip}
						onChange={(e) => onSkipChange(e.target.checked)}
						disabled={locked}
						label="Skip on upload"
						dataTestId={`${testId}-skip`}
					/>
					<SystemButton onClick={onUpload} disabled={uploadDisabled} loading={isLoading} data-testid={`${testId}-upload`}>
						{uploadLabel}
					</SystemButton>
					<SystemButton buttonVariant="secondary" onClick={onRemove} disabled={locked} data-testid={`${testId}-remove`}>
						Remove
					</SystemButton>
				</Paper>
			)}

			{file.loadResult && (
				<Box sx={{ mt: 2 }}>
					{file.kind === "roster" ? (
						<>
							{/* Side effects on OTHER nurses and on the department, which NursesSummary does not report. */}
							<NurseLoadSideEffects results={file.loadResult as LoadNursesResults} dataTestId={testId} />
							<RosterLoadResults results={file.loadResult as LoadNursesResults} timezone={timezone} dataTestId={testId} />
						</>
					) : (
						<LoadResultDisplay result={file.loadResult as LoadShiftsResult | LoadRecurringResult} departmentId={departmentId} timezone={timezone} />
					)}
				</Box>
			)}
		</Box>
	);
}
